import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  IconButton,
  Avatar,
  Card,
  CardContent,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  DeleteForever as DeleteIcon,
  Logout as LogoutIcon,
  Save as SaveIcon,
  Email as EmailIcon,
  AccountCircle as AccountIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  
  // Delete account dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Logout confirmation dialog state
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const handleChangePassword = async () => {
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }
    
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    setIsChangingPassword(true);
    setPasswordError('');
    setPasswordSuccess('');
    
    try {
      await axios.post('/api/users/me/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      
      setPasswordSuccess('Password changed successfully! Please login again with your new password.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Clear tokens and redirect to login after 2 seconds
      setTimeout(() => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        navigate('/login');
      }, 2000);
    } catch (error: any) {
      console.error('Failed to change password:', error);
      if (error.response?.status === 400) {
        setPasswordError('Current password is incorrect');
      } else {
        setPasswordError(error.response?.data?.detail || 'Failed to change password. Please try again.');
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleDeleteAccount = () => {
    setDeleteDialogOpen(false);
    // Show support contact info instead of actually deleting
    alert('To delete your account, please contact our support team at support@quickbuildr.ai');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard')}
            sx={{ mb: 2, textTransform: 'none' }}
          >
            Back to Dashboard
          </Button>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Account Settings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your account information and security settings
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Profile Information Card */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                height: '100%',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  Profile Information
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                <Avatar
                  sx={{ 
                    width: 100, 
                    height: 100, 
                    bgcolor: 'primary.main',
                    fontSize: '2.5rem',
                    mb: 2,
                  }}
                >
                  {user?.full_name?.[0] || user?.username?.[0] || 'U'}
                </Avatar>
                <Typography variant="h6" fontWeight={600}>
                  {user?.full_name || user?.username}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <VerifiedIcon sx={{ fontSize: 16, color: 'primary.main', mr: 0.5 }} />
                  <Typography variant="body2" color="primary.main" fontWeight={500}>
                    Pro Account
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <EmailIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      Email
                    </Typography>
                  </Box>
                  <Typography variant="body1">
                    {user?.email}
                  </Typography>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AccountIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      Username
                    </Typography>
                  </Box>
                  <Typography variant="body1">
                    {user?.username}
                  </Typography>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AccountIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      Member Since
                    </Typography>
                  </Box>
                  <Typography variant="body1">
                    {user?.created_at ? formatDate(user.created_at) : 'N/A'}
                  </Typography>
                </CardContent>
              </Card>
            </Paper>
          </Grid>

          {/* Settings Column */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Change Password Card */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <LockIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight={600}>
                    Change Password
                  </Typography>
                </Box>

                {passwordError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {passwordError}
                  </Alert>
                )}

                {passwordSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {passwordSuccess}
                  </Alert>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Current Password"
                    type="password"
                    fullWidth
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    variant="outlined"
                  />
                  <TextField
                    label="New Password"
                    type="password"
                    fullWidth
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    variant="outlined"
                    helperText="Must be at least 8 characters"
                  />
                  <TextField
                    label="Confirm New Password"
                    type="password"
                    fullWidth
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    variant="outlined"
                  />
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleChangePassword}
                    disabled={isChangingPassword}
                    sx={{ mt: 1, textTransform: 'none' }}
                  >
                    {isChangingPassword ? 'Changing Password...' : 'Change Password'}
                  </Button>
                </Box>
              </Paper>

              {/* Account Actions Card */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Account Actions
                </Typography>
                
                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Logout
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Sign out of your account on this device
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<LogoutIcon />}
                      onClick={() => setLogoutDialogOpen(true)}
                      sx={{ textTransform: 'none' }}
                    >
                      Logout
                    </Button>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom color="error">
                      Delete Account
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Permanently delete your account and all associated data. This action cannot be undone.
                    </Typography>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => setDeleteDialogOpen(true)}
                      sx={{ textTransform: 'none' }}
                    >
                      Delete Account
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </motion.div>

      {/* Delete Account Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Account?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            To delete your account, please contact our support team at{' '}
            <strong>support@quickbuildr.ai</strong>. We'll help you with the account deletion process
            and answer any questions you may have.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Close
          </Button>
          <Button 
            onClick={() => {
              window.location.href = 'mailto:support@quickbuildr.ai?subject=Account Deletion Request';
              setDeleteDialogOpen(false);
            }}
            variant="contained"
            sx={{ textTransform: 'none' }}
          >
            Contact Support
          </Button>
        </DialogActions>
      </Dialog>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
      >
        <DialogTitle>Logout?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to logout from your account?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button 
            onClick={handleLogout}
            variant="contained"
            color="primary"
            sx={{ textTransform: 'none' }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProfilePage;
